import { clearsEmptyProperties } from '@utils/functions/clearsEmptyProperties'

import { API_URLS } from '@services/apiRoutes'

import type {
  AddEditOtherConsumptionRequestProps,
  ApprovalWorkflowConsumptionRequestProps,
  EditDetailedSummaryRequestProps,
  GetBigNumbersConsumptionRequestProps,
  GetConsumptionAveragePercentagesRequestProps,
  GetClassifiedConsumptionRequestProps,
  GetDetailedSummaryRequestProps,
  GetDetailedSummaryV2RequestProps,
  GetOtherConsumptionsRequestProps,
  GetWeeklyBreakdownRequestProps,
  ReleasePreviewConsumptionRequestProps,
  RemoveOtherConsumptionRequestProps
} from './types'

import * as apiFunction from '@services/apiFunctions'

export const reversePreviewConsumption = async (payload: ReleasePreviewConsumptionRequestProps) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.consumptionV2}/${payload.filiCode}/reverter-previa-semanal/dt-competencia/${payload.competenceDate}`,
    method: 'PATCH'
  })
}

export const releasePreviewConsumption = async (payload: ReleasePreviewConsumptionRequestProps) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.consumptionV2}/${payload.filiCode}/liberar-previa-semanal/dt-competencia/${payload.competenceDate}`,
    method: 'PATCH'
  })
}

export const reverseWorkflowConsumption = async (payload: ApprovalWorkflowConsumptionRequestProps) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.consumption}/${payload.filiCode}/restaurar-previa/dt-competencia/${payload.competenceDate}`,
    method: 'DELETE'
  })
}

export const approvalWorkflowConsumption = async (payload: ApprovalWorkflowConsumptionRequestProps) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.consumption}/${payload.filiCode}/envio-previa/dt-competencia/${payload.competenceDate}`,
    method: 'PATCH'
  })
}

export const getBigNumbersConsumption = async (payload: GetBigNumbersConsumptionRequestProps) => {
  return await apiFunction.apiRequestWithQueryParams({
    url: `${API_URLS.finapi.consumptionV2}/${payload.filiCode}/detalhe-previa/dt-competencia/${payload.competenceDate}`,
    method: 'GET'
  })
}

export const getConsumptionAveragePercentages = async (payload: GetConsumptionAveragePercentagesRequestProps) => {
  return await apiFunction.apiRequestWithQueryParams({
    url: `${API_URLS.finapi.consumptionV2}/${payload.filiCode}/consumption-average-percentages?competenceDate=${payload.competenceDate}`,
    method: 'GET'
  })
}

export const getWeeklyBreakdown = async (payload: GetWeeklyBreakdownRequestProps) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.consumption}/${payload.filiCode}/busca-consumo-agrupado`,
    method: 'POST',
    body: {
      sortBy: payload.sortBy,
      sortType: payload.sortType || '',
      consumptionReferenceDate: payload.competenceDate,
      currentPage: payload.page,
      itemsPerPage: payload.itemsPerPage,
      supplierCodeFilter: payload.supplierCodeFilter,
      originFilter: payload.originFilter,
      itemClassificationFilter: payload.itemClassificationFilter,
      operationCodeFilter: payload.operationCodeFilter
    }
  })
}

export const editDetailedSummary = async (payload: EditDetailedSummaryRequestProps) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.consumptionV2}/${payload.branchCode}/consumption-detailed-summary/adjustment?branchCode=${payload.branchCode}`,
    method: 'PUT',
    body: {
      weekNumber: payload.weekNumber,
      categoryKey: payload.categoryKey,
      adjustmentValue: payload.adjustmentValue,
      observations: payload.observations,
      competenceDate: payload.competenceDate
    }
  })
}

export const getDetailedSummaryV2 = async (payload: GetDetailedSummaryV2RequestProps) => {
  return await apiFunction.apiRequestWithQueryParams({
    url: `${API_URLS.finapi.consumptionV2}/${payload.filiCode}/consumption-detailed-summary?branchCode=${payload.filiCode}&competenceDate=${payload.competenceDate}`,
    method: 'GET'
  })
}

export const getDetailedSummary = async (payload: GetDetailedSummaryRequestProps) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.consumption}/${payload.filiCode}/busca-resumo-detalhado`,
    method: 'POST',
    body: {
      competenceDate: payload.competenceDate
    }
  })
}

export const getClassifiedConsumption = async (payload: GetClassifiedConsumptionRequestProps) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.consumption}/${payload.filiCode}/busca-consumo-classificado`,
    method: 'POST',
    body: {
      competenceDate: payload.competenceDate
    }
  })
}

export const removeOtherConsumption = async (payload: RemoveOtherConsumptionRequestProps) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.consumption}/${payload.filiCode}/outros-consumos/${payload.consumptionId}/${payload.competenceDate}`,
    method: 'DELETE'
  })
}

export const editOtherConsumption = async (payload: AddEditOtherConsumptionRequestProps) => {
  const data = clearsEmptyProperties(payload)
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.consumption}/${payload.filiCode}/outros-consumos/${payload.id}`,
    method: 'PUT',
    body: {
      consumptionReferenceDate: data.competenceDate,
      consumptionTypeId: data.consumptionTypeId,
      observations: data.observations,
      releaseDate: data.date,
      totalValue: data.totalValue
    }
  })
}

export const createNewOtherConsumption = async (payload: AddEditOtherConsumptionRequestProps) => {
  const data = clearsEmptyProperties(payload)
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.consumption}/${payload.filiCode}/outros-consumos`,
    method: 'POST',
    body: {
      consumptionReferenceDate: data.competenceDate,
      consumptionTypeId: data.consumptionTypeId,
      observations: data.observations,
      releaseDate: data.date,
      totalValue: data.totalValue
    }
  })
}

export const getOtherConsumptions = async (payload: GetOtherConsumptionsRequestProps) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.consumption}/${payload.filiCode}/busca-outros-consumos`,
    method: 'POST',
    body: {
      consumptionReferenceDate: payload.competenceDate,
      currentPage: payload.page,
      itemsPerPage: payload.itemsPerPage,
      sortBy: payload.sortBy,
      sortType: payload.sortType || ''
    }
  })
}

export const getConsumptionTypes = async () => {
  return await apiFunction.apiRequestWithQueryParams({
    url: `${API_URLS.finapi.consumption}/tipos-consumo`,
    method: 'GET'
  })
}
